import { useState } from 'react';
import { isAxiosError } from 'axios';
import { Button } from '@/app/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/app/components/ui/card';
import { usePermissions } from '@/hooks/use-permissions';
import { usePlatformModules } from '@/hooks/use-modules';
import { useTechPayRates } from '@/hooks/use-payroll';
import { useOrgCurrency } from '@/hooks/use-settings';
import { formatMoney } from '@/lib/currency';
import { formatDateOnly } from '@/lib/date-utils';
import type { Project, TechPayRate } from '@/lib/types';
import { PayRateForm } from './pay-rate-form';

export function JobPayRatesPanel({ project }: { project: Project }) {
  const permissions = usePermissions();
  const { isModuleEnabled } = usePlatformModules();
  const canViewRates =
    isModuleEnabled('payroll') &&
    (permissions.isAdmin || permissions.hasMinModuleRole('payroll', 'manager'));
  const { data: rates = [], isLoading, error } = useTechPayRates(
    { project: project.id },
    canViewRates,
  );
  const currency = useOrgCurrency();
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRate, setEditingRate] = useState<TechPayRate | null>(null);

  if (!canViewRates || (isAxiosError(error) && error.response?.status === 403)) {
    return null;
  }

  const fixedProject = { id: project.id, name: project.name };
  const closeForm = () => {
    setShowAddForm(false);
    setEditingRate(null);
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Pay rates</CardTitle>
          <Button onClick={() => {
            setEditingRate(null);
            setShowAddForm(true);
          }}>
            Add rate
          </Button>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="p-6 text-sm text-gray-500">Loading pay rates...</p>
          ) : error ? (
            <p className="p-6 text-sm text-gray-500">Couldn&apos;t load pay rates.</p>
          ) : rates.length === 0 ? (
            <p className="p-6 text-sm text-gray-500">No pay rates for this job yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="px-4 pb-2">Applies to</th>
                    <th className="px-4 pb-2">Effective date</th>
                    <th className="px-4 pb-2 text-right">Hourly</th>
                    <th className="px-4 pb-2 text-right">Overtime</th>
                    <th className="px-4 pb-2 text-right">Daily</th>
                    <th className="px-4 pb-2 text-right">Per diem</th>
                    <th className="px-4 pb-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((rate) => (
                    <tr key={rate.id} className="border-b">
                      <td className="px-4 py-2">
                        {rate.person == null && rate.tech == null
                          ? 'Everyone on this job'
                          : rate.person_name ?? rate.tech_name ?? 'Unknown'}
                      </td>
                      <td className="px-4 py-2">{formatDateOnly(rate.effective_date)}</td>
                      <td className="px-4 py-2 text-right">{formatMoney(rate.hourly_rate, currency)}</td>
                      <td className="px-4 py-2 text-right">{formatMoney(rate.overtime_rate, currency)}</td>
                      <td className="px-4 py-2 text-right">{formatMoney(rate.daily_rate, currency)}</td>
                      <td className="px-4 py-2 text-right">{formatMoney(rate.per_diem_rate, currency)}</td>
                      <td className="px-4 py-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setShowAddForm(false);
                            setEditingRate(rate);
                          }}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      <p className="text-sm text-gray-500">
        Rates for this job are saved now but aren&apos;t used for pay yet. They take effect in a later update.
      </p>
      {showAddForm && <PayRateForm fixedProject={fixedProject} onDone={closeForm} />}
      {editingRate && (
        <PayRateForm
          key={editingRate.id}
          fixedProject={fixedProject}
          rate={editingRate}
          onDone={closeForm}
        />
      )}
    </div>
  );
}
